import { graphlib, layout } from '@dagrejs/dagre'
import type { Edge, Node } from '@vue-flow/core'
import type {
  PolicyInsight,
  PredicateData,
} from '@worker/policy-builder/client'

export const NODE_WIDTH = 300
export const NODE_HEIGHT = 140
const X_GAP = 120
const Y_GAP = 100

export type PredicateTreeNode =
  | Node<PredicateData, {}, 'predicate'>
  | Node<PolicyInsight, {}, 'insight'>

/*
 * ponytail: this dagre layout is a copy of stores/engine-designer.ts's watcher
 * (positions predicates and insights, then nudges yes/no siblings apart)
 * rather than a shared extraction — that store's copy is entangled with the
 * builder's Pinia lifecycle (IndexedDB draft persistence, dirty tracking),
 * too much to pull the room-backed collaborative tree into. Ceiling: the two
 * copies can drift. Upgrade path: extract both onto this pure function once
 * the builder store no longer needs its own.
 */
export const layoutPredicateTree = (
  nodes: PredicateData[],
  insights: PolicyInsight[],
  direction: 'LR' | 'TB',
): { nodes: PredicateTreeNode[]; edges: Edge[] } => {
  const graph = new graphlib.Graph()
  graph.setGraph({ rankdir: direction, nodesep: X_GAP, ranksep: Y_GAP })
  graph.setDefaultEdgeLabel(() => ({}))

  const targetFor = (path: string) =>
    insights.find((insight) => insight.label === path)?.id ??
    nodes.find((predicate) => predicate.label === path)?.id

  const edges: Edge[] = []
  const insightNodes = insights.map((insight) => {
    graph.setNode(insight.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
    return {
      id: insight.id,
      type: 'insight' as const,
      data: insight,
      position: { x: 0, y: 0 },
    }
  })
  const predicateNodes = nodes.map((predicate) => {
    graph.setNode(predicate.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
    return {
      id: predicate.id,
      type: 'predicate' as const,
      data: predicate,
      position: { x: 0, y: 0 },
    }
  })

  for (const node of predicateNodes) {
    const yesPath = (node.data.yesPath || '').trim()
    const noPath = (node.data.noPath || '').trim()
    if (yesPath) {
      const target = targetFor(yesPath)
      if (target) {
        edges.push({
          id: `e-${node.id}->${target}:Y`,
          source: node.id,
          sourceHandle: 'predicate-node-yes-path',
          target,
          label: 'Yes',
        })
        graph.setEdge(node.id, target)
      }
    }
    if (noPath) {
      const target = targetFor(noPath)
      if (target) {
        edges.push({
          id: `e-${node.id}->${target}:N`,
          source: node.id,
          sourceHandle: 'predicate-node-no-path',
          target,
          label: 'No',
        })
        graph.setEdge(node.id, target)
      }
    }
  }

  layout(graph)

  // Post-process node positions (dagre doesn't support sibling ordering).
  for (const node of predicateNodes) {
    const yesPath = (node.data.yesPath || '').trim()
    const noPath = (node.data.noPath || '').trim()
    if (!yesPath || !noPath) continue
    const yesTargetId = targetFor(yesPath)
    const noTargetId = targetFor(noPath)
    if (!yesTargetId || !noTargetId || yesTargetId === noTargetId) continue

    // graph.node() always resolves for these three ids: the parent is one of
    // predicateNodes, and both targets came from targetFor, which only
    // returns ids of nodes already added via setNode above.
    const parentNode = graph.node(node.id)!
    const yesNode = graph.node(yesTargetId)!
    const noNode = graph.node(noTargetId)!

    if (direction === 'LR') {
      const spread = Math.abs(yesNode.y - noNode.y)
      const minOffset = NODE_HEIGHT / 2 + Y_GAP / 2
      yesNode.y = parentNode.y - Math.max(spread / 2, minOffset)
      noNode.y = parentNode.y + Math.max(spread / 2, minOffset)
    } else {
      const spread = Math.abs(yesNode.x - noNode.x)
      const minOffset = NODE_WIDTH / 2 + X_GAP / 2
      yesNode.x = parentNode.x + Math.max(spread / 2, minOffset)
      noNode.x = parentNode.x - Math.max(spread / 2, minOffset)
    }
  }

  // graph.node(id) always resolves here: every id passed in was added via
  // setNode above, and dagre assigns every node a position after layout().
  const position = (id: string) => {
    const point = graph.node(id)!
    return { x: point.x, y: point.y }
  }

  return {
    nodes: [
      ...predicateNodes.map((node) => ({
        ...node,
        position: position(node.id),
      })),
      ...insightNodes.map((node) => ({ ...node, position: position(node.id) })),
    ],
    edges,
  }
}
